import { Button } from "../ui/button";
import { ArrowRight, Sparkles, ShieldCheck, Clock, MessageCircle, Mail } from "lucide-react";
import { openLiveChat } from "../../lib/chat";
import { OLIVEUP_HELLO_EMAIL, OLIVEUP_MAILTO } from "../../lib/contact";

export default function Hero() {
  const goToApply = () =>
    document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-10 pb-24 md:pt-16 md:pb-32"
    >
      {/* Soft blobs — slow drift keeps the hero alive without stealing focus */}
      <div
        className="blob blob-drift-a absolute -top-24 -left-24 h-[28rem] w-[28rem] rounded-full bg-[#D9DEC1]"
        aria-hidden
      />
      <div
        className="blob blob-drift-b absolute -bottom-32 -right-20 h-[24rem] w-[24rem] rounded-full bg-[#F2C7B6]"
        aria-hidden
      />

      <div className="container-tight relative grid lg:grid-cols-12 gap-12 items-center">
        {/* Left copy */}
        <div className="lg:col-span-7">
          <div
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur border border-border px-4 py-2 mb-7 animate-fade-up"
          >
            <Sparkles className="h-3.5 w-3.5 text-[#F5B82E] animate-olive-sparkle inline-block" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              The Fun-Loving Funding Team
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display text-5xl sm:text-7xl lg:text-[5.5rem] font-black tracking-tight text-foreground leading-[1.02] text-balance animate-fade-up delay-1"
          >
            Get{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-primary italic">Olive</span>
              <span
                aria-hidden
                className="hero-olive-underline absolute left-0 right-0 bottom-1 h-3 bg-[#F2C7B6] -z-0 rounded-sm"
              />
            </span>{" "}
            that funding.
          </h1>

          <p
            data-testid="hero-subhead"
            className="mt-7 max-w-xl text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-up delay-2"
          >
            Whether your credit is excellent, still building, or has a few
            battle scars — we&apos;re the fun-loving funding team that says{" "}
            <em>YES</em> when others say no. Fast turnarounds, flexible terms,
            and a real human in your corner. Get OliveUp. Get it today.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4 animate-fade-up delay-3">
            <Button
              data-testid="hero-apply-btn"
              onClick={goToApply}
              className="group rounded-full bg-accent hover:bg-[#D1664A] text-accent-foreground hover:text-accent-foreground h-14 px-8 text-base font-semibold shadow-[0_4px_14px_0_rgb(224,122,95,0.39)] hover:-translate-y-0.5 transition-all"
            >
              Get pre-qualified in 2 minutes
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button
              data-testid="hero-products-btn"
              variant="outline"
              onClick={() =>
                document
                  .getElementById("funding")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="rounded-full h-14 px-8 text-base font-semibold border-foreground/20 text-foreground hover:bg-foreground hover:text-background"
            >
              See funding products
            </Button>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md animate-fade-up delay-4">
            <Stat icon={<Clock className="h-4 w-4" />} value="24h" label="Avg. funding" />
            <Stat icon={<ShieldCheck className="h-4 w-4" />} value="All" label="Credit types ok" />
            <Stat icon={<Sparkles className="h-4 w-4" />} value="$20M" label="Up to in capital" />
          </div>
        </div>

        {/* Right collage — clean 3-photo mosaic */}
        <div className="lg:col-span-5 relative animate-fade-up delay-2">
          <div className="relative grid grid-cols-6 grid-rows-6 gap-3 h-[520px] animate-olive-collage">
            <div
              className="col-span-4 row-span-6 rounded-3xl overflow-hidden bg-muted shadow-[0_8px_30px_rgb(0,0,0,0.08)]"
              style={{
                backgroundImage:
                  "url('https://images.pexels.com/photos/4473398/pexels-photo-4473398.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              data-testid="hero-image-1"
            />
            <div
              className="col-span-2 row-span-3 col-start-5 rounded-3xl overflow-hidden bg-muted shadow-[0_8px_30px_rgb(0,0,0,0.08)]"
              style={{
                backgroundImage:
                  "url('https://images.unsplash.com/photo-1628633966331-6deec26ce667?crop=entropy&cs=srgb&fm=jpg&w=600&q=80')",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              data-testid="hero-image-2"
            />
            <a
              href="#apply"
              data-testid="hero-stat-card"
              className="col-span-2 row-span-3 col-start-5 row-start-4 rounded-3xl bg-primary text-primary-foreground p-6 shadow-[0_8px_30px_rgb(0,0,0,0.10)] flex flex-col justify-between transition-all hover:-translate-y-1 hover:bg-[#3B4A1C] group"
            >
              <span className="text-xs uppercase tracking-[0.2em] font-semibold opacity-85">
                Small businesses served
              </span>
              <div>
                <div className="font-display text-5xl font-black leading-none text-[#F5B82E]">
                  10K+
                </div>
                <p className="text-sm opacity-90 mt-2 leading-snug">
                  Owners across 50+ industries — and growing daily.
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#F5B82E] group-hover:gap-2.5 transition-all">
                  Join them <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </a>
          </div>

          <div
            data-testid="hero-contact-row"
            className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground animate-fade-up delay-3"
          >
            <button
              type="button"
              onClick={openLiveChat}
              data-testid="hero-chat-link"
              className="inline-flex items-center gap-2 font-semibold text-primary hover:text-[#3B4A1C] transition-colors"
            >
              <MessageCircle className="h-4 w-4 shrink-0" />
              Questions first? Live chat
            </button>
            <a
              href={OLIVEUP_MAILTO}
              data-testid="hero-email-link"
              className="inline-flex items-center gap-2 font-semibold text-primary hover:text-[#3B4A1C] transition-colors"
            >
              <Mail className="h-4 w-4 shrink-0" />
              {OLIVEUP_HELLO_EMAIL}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ icon, value, label }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-primary">{icon}</div>
      <div className="font-display text-2xl font-extrabold text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
